import React, { useState } from 'react';
import styled from '@xstyled/styled-components';
import { uniqueId } from '~/lib/uniqueId';
import { VisuallyHiddenLegend } from '~/lib/a11y/VisuallyHidden';
import { useToggleAudioEffect } from '../audio-effect/useToggleAudioEffect';
import { PickListItem } from './PickListItem';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ItemType = { [k: string]: any } | string | number;

interface PickListProps<T> {
  items: T[];
  selected?: T;
  onSelect(item: T): void;
  labelField?: string;
  valueField?: string;
  additionalField?: string;
  useSubtitle?: boolean;
  /**
   * CAT-1298 / EL–E130: group radios in a fieldset.
   * Visible `<legend>` text, or omit and pass `aria-label` for a visually hidden legend.
   */
  legend?: string;
  /** Hide the legend visually while keeping it for assistive tech. */
  legendVisuallyHidden?: boolean;
  /**
   * Parent already provides `<fieldset>` + `<legend>` (e.g. settings pages).
   * Renders only the radio options — avoids nested fieldsets.
   */
  ungrouped?: boolean;
  /** Accessible name when `legend` is omitted (rendered as VisuallyHiddenLegend). */
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

/** Reset UA fieldset chrome so callers keep existing visual layout. */
const Fieldset = styled.fieldset`
  border: 0;
  margin: 0;
  padding: 0;
  min-width: 0;
`;

const VisibleLegend = styled.legend`
  padding: 0;
  margin: 0 0 12px;
  font-size: 14px;
  font-weight: 600;
`;

function getVal<T extends ItemType>(item: T, fieldName?: string) {
  if (typeof item === 'string' || typeof item === 'number') return item;
  if (!fieldName) throw new Error('need field name for list of objects');
  return item[fieldName].toString();
}

export const PickList = <T extends ItemType>({
  items,
  selected,
  onSelect,
  labelField,
  valueField,
  useSubtitle,
  additionalField,
  legend,
  legendVisuallyHidden = false,
  ungrouped = false,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy
}: PickListProps<T>) => {
  const [name] = useState(uniqueId('radio'));
  const [playAudioEffect] = useToggleAudioEffect();

  items = items.sort(function (a, b) {
    if (a < b) {
      return -1;
    }
    if (a > b) {
      return 1;
    }
    return 0;
  });

  const options = items.map(i => {
    const value = getVal(i, valueField);
    const label = getVal(i, labelField);
    const additionalLabel = additionalField ? getVal(i, additionalField) : null;

    return (
      <PickListItem
        key={value}
        value={value}
        additionalValue={useSubtitle ? additionalLabel : ''}
        checked={selected === i}
        name={name}
        onChange={e => {
          try {
            playAudioEffect();
          } catch (error) {
            // Silently ignore audio effect errors
          }
          if (e.target.value === value) onSelect(i);
        }}
        onKeyPress={() => {
          try {
            playAudioEffect();
          } catch (error) {
            // Silently ignore audio effect errors
          }
          onSelect(i);
        }}
      >
        {label}
      </PickListItem>
    );
  });

  // Parent owns fieldset + legend (settings Language / Theme / Playback Speed).
  if (ungrouped) {
    return <>{options}</>;
  }

  const legendText = legend?.trim() || ariaLabel?.trim() || '';
  const hideLegend = legendVisuallyHidden || (!legend && Boolean(ariaLabel));

  return (
    <Fieldset aria-labelledby={ariaLabelledBy}>
      {/*
        CAT-1298 / EL–E130: legend must be the first child of fieldset.
        Prefer a visible legend; fall back to visually hidden from aria-label.
      */}
      {legendText ? (
        hideLegend ? (
          <VisuallyHiddenLegend>{legendText}</VisuallyHiddenLegend>
        ) : (
          <VisibleLegend>{legendText}</VisibleLegend>
        )
      ) : null}
      {options}
    </Fieldset>
  );
};
